'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect
} from 'react';
import {USER_ROLES} from '#utils/constants';
import {getFromLS, setToLS} from '#utils/localStorage';

// Form data interfaces
interface PersonalData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  address: string;
  city: string;
  postalCode: string;
  country: string;
  googleAuthToken?: string;
  facebookAuthToken?: string;
  prefilled?: {
    email?: string;
    first_name?: string;
    last_name?: string;
  };
}

interface OrganisationData {
  name: string;
  orgNumber: string;
  orgType: string;
  orgSize: string;
  phone?: string;
  email?: string;
  password?: string;
  description?: string;
  categoryIds?: number[];
  website?: string;
  logo?: string;
  address?: {
    formatted?: string;
    street?: string;
    country?: string;
    city?: string;
    zip?: string;
    apartment?: string;
    is_main?: boolean;
    is_verified?: boolean;
  };
  organisationId?: number;
  contactPerson: {
    name: string;
    email: string;
    phone: string;
  };
  googleAuthToken?: string;
  facebookAuthToken?: string;
  prefilled?: {
    email?: string;
    first_name?: string;
    last_name?: string;
  };
}

interface MerchantData {
  businessName: string;
  businessType: string;
  businessNumber: string;
  description?: string;
  id: number;
  uniqueMerchantId?: string;
  phone?: string;
  website?: string;
  logo?: string;
  address?: {
    formatted?: string;
    street?: string;
    country?: string;
    city?: string;
    zip?: string;
    apartment?: string;
    is_main?: boolean;
    is_verified?: boolean;
  };
  contactPerson: {
    name: string;
    email: string;
    phone: string;
  };
  b2cCategories: number[];
  googleAuthToken?: string;
  facebookAuthToken?: string;
  prefilled?: {
    email?: string;
    first_name?: string;
    last_name?: string;
  };
}

interface AuthFormState {
  // Registration flow
  currentStep: number;
  accountType: USER_ROLES | '';

  // Personal data
  personalData: Partial<PersonalData>;

  // Organisation data
  organisationData: Partial<OrganisationData>;

  // Merchant data
  merchantData: Partial<MerchantData>;

  // Password reset flow
  resetPasswordFlow: {
    currentStep: number;
    isEmail: boolean;
    isCheckEmail: boolean;
    isReset: boolean;
    isComplete: boolean;
    email: string;
  };
}

interface AuthFormContextType extends AuthFormState {
  // Registration actions
  setCurrentStep: (step: number) => void;
  setAccountType: (type: USER_ROLES) => void;

  // Personal data actions
  updatePersonalData: (data: Partial<PersonalData>) => void;
  resetPersonalData: () => void;

  // Organisation data actions
  updateOrganisationData: (data: Partial<OrganisationData>) => void;
  resetOrganisationData: () => void;

  // Merchant data actions
  updateMerchantData: (data: Partial<MerchantData>) => void;
  resetMerchantData: () => void;

  // Password reset actions
  updateResetPasswordFlow: (
    data: Partial<AuthFormState['resetPasswordFlow']>
  ) => void;
  resetResetPasswordFlow: () => void;

  // General actions
  resetAll: () => void;
}

const STORAGE_KEY = 'authFormState';

// Default state to avoid duplication
const defaultState: AuthFormState = {
  currentStep: 0,
  accountType: '' as USER_ROLES,
  personalData: {},
  organisationData: {},
  merchantData: {},
  resetPasswordFlow: {
    currentStep: 0,
    isEmail: false,
    isCheckEmail: false,
    isReset: false,
    isComplete: false,
    email: ''
  }
};

const getInitialState = (): AuthFormState => {
  if (typeof window === 'undefined') {
    return defaultState;
  }

  const stored = getFromLS(STORAGE_KEY) as Partial<AuthFormState> | null;

  if (stored) {
    return {
      ...defaultState,
      ...stored
    };
  }

  return defaultState;
};

const AuthFormContext = createContext<AuthFormContextType | undefined>(
  undefined
);

export function AuthFormProvider({children}: {children: React.ReactNode}) {
  // Use lazy initializer to read from localStorage on mount
  const [state, setState] = useState<AuthFormState>(() => getInitialState());

  useEffect(() => {
    setToLS(STORAGE_KEY, state);
  }, [state]);

  // Registration actions
  const setCurrentStep = useCallback((step: number) => {
    setState(prev => ({...prev, currentStep: step}));
  }, []);

  const setAccountType = useCallback((type: USER_ROLES) => {
    setState(prev => ({...prev, accountType: type}));
  }, []);

  // Personal data actions
  const updatePersonalData = useCallback((data: Partial<PersonalData>) => {
    setState(prev => ({
      ...prev,
      personalData: {...prev.personalData, ...data}
    }));
  }, []);

  const resetPersonalData = useCallback(() => {
    setState(prev => ({...prev, personalData: {}}));
  }, []);

  // Organisation data actions
  const updateOrganisationData = useCallback(
    (data: Partial<OrganisationData>) => {
      setState(prev => ({
        ...prev,
        organisationData: {...prev.organisationData, ...data}
      }));
    },
    []
  );

  const resetOrganisationData = useCallback(() => {
    setState(prev => ({...prev, organisationData: {}}));
  }, []);

  // Merchant data actions
  const updateMerchantData = useCallback((data: Partial<MerchantData>) => {
    setState(prev => ({
      ...prev,
      merchantData: {...prev.merchantData, ...data}
    }));
  }, []);

  const resetMerchantData = useCallback(() => {
    setState(prev => ({...prev, merchantData: {}}));
  }, []);

  // Password reset actions
  const updateResetPasswordFlow = useCallback(
    (data: Partial<AuthFormState['resetPasswordFlow']>) => {
      setState(prev => ({
        ...prev,
        resetPasswordFlow: {...prev.resetPasswordFlow, ...data}
      }));
    },
    []
  );

  const resetResetPasswordFlow = useCallback(() => {
    setState(prev => ({
      ...prev,
      resetPasswordFlow: defaultState.resetPasswordFlow
    }));
  }, []);

  // General actions
  const resetAll = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // Ignore errors
    }
    setState(defaultState);
  }, []);

  return (
    <AuthFormContext.Provider
      value={{
        ...state,
        setCurrentStep,
        setAccountType,
        updatePersonalData,
        resetPersonalData,
        updateOrganisationData,
        resetOrganisationData,
        updateMerchantData,
        resetMerchantData,
        updateResetPasswordFlow,
        resetResetPasswordFlow,
        resetAll
      }}
    >
      {children}
    </AuthFormContext.Provider>
  );
}

export function useAuthForm() {
  const context = useContext(AuthFormContext);
  if (context === undefined) {
    throw new Error('useAuthForm must be used within an AuthFormProvider');
  }
  return context;
}
